import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../lib/axios';
import { useAuth } from './AuthContext';


interface Notification {
    id: string;
    message: string;
    title?: string;
    type: 'appointment' | 'reminder' | 'alert' | 'announcement' | 'lab' | 'admission';
    appointmentId?: string;
    trainingBookingId?: string;
    orderId?: string;
    dayCareBookingId?: string;
    path?: string;
    kind?: string;
    createdAt: string;
    read: boolean;
}

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    markAsRead: (id: string, type?: string) => void;
    markAllAsRead: () => void;
    clearAllNotifications: () => void;
    refreshNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const fetchNotifications = async () => {
        try {
            // Fetch both standard notifications and partner announcements
            const [notifRes, annRes] = await Promise.all([
                api.get('/notifications'),
                api.get('/announcements/my').catch(() => ({ data: [] })) // Fallback if announcements fail
            ]);

            // Transform standard notifications
            let standardNotifs = notifRes.data.map((n: any) => ({
                id: n.id,
                title: n.title,
                message: n.message,
                type: n.kind === 'APPOINTMENT' ? 'appointment'
                    : n.kind === 'LAB' ? 'lab'
                    : n.kind === 'ADMISSION' ? 'admission'
                    : 'alert',
                appointmentId: n.appointmentId,
                trainingBookingId: n.trainingBookingId,
                orderId: n.orderId,
                dayCareBookingId: n.dayCareBookingId,
                path: n.path,
                createdAt: n.createdAt,
                read: n.isRead,
                kind: n.kind // Keep kind for filtering
            }));

            // Client-side filtering as fallback/augmentation to backend
            if (user?.role === 'DOCTOR') {
                standardNotifs = standardNotifs.filter((n: any) =>
                    !n.message.toLowerCase().includes('grooming') &&
                    n.kind !== 'GROOMING'
                );
            } else if (user?.role === 'GROOMER' || user?.role === 'GROOMING_AGENCY') {
                standardNotifs = standardNotifs.filter((n: any) =>
                    !n.message.toLowerCase().includes('surgery') &&
                    !n.message.toLowerCase().includes('vaccination') &&
                    n.kind !== 'SURGERY'
                );
            } else if (user?.role === 'PARTNER') {
                // Partners should only see partner-specific or system notifications, not hospital/clinic updates
                standardNotifs = standardNotifs.filter((n: any) =>
                    (n.kind && n.kind.startsWith('PARTNER')) || n.kind === 'SYSTEM' || n.kind === 'ALERT'
                );
            }

            // Transform announcements
            const announcements = (annRes.data || []).map((a: any) => ({
                id: a.announcement.id, // Use announcement ID for marking read
                title: a.announcement.title,
                message: a.announcement.message,
                type: 'announcement' as const,
                createdAt: a.announcement.sentAt,
                read: a.isRead
            }));

            // Merge and sort
            const merged = [...standardNotifs, ...announcements].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            setNotifications(merged);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    useEffect(() => {
        if (user) {
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 5 * 1000); // Poll every 5 seconds for "real-time"
            return () => clearInterval(interval);
        }
    }, [user]);

    const markAsRead = async (id: string, type?: string) => {
        try {
            if (type === 'announcement') {
                await api.put(`/announcements/${id}/read`);
            } else {
                await api.patch(`/notifications/${id}/read`);
            }

            setNotifications(prev =>
                prev.map(n => (n.id === id ? { ...n, read: true } : n))
            );
        } catch (error) {
            console.error("Failed to mark read", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            // Mark all standard read
            await api.patch(`/notifications/read-all`);

            // Mark all announcements read (we don't have a read-all for announcements yet)
            const unreadAnnouncements = notifications.filter(n => n.type === 'announcement' && !n.read);
            await Promise.all(unreadAnnouncements.map(a => api.put(`/announcements/${a.id}/read`)));

            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch (error) {
            console.error("Failed to mark all read", error);
        }
    };
    
    const clearAllNotifications = async () => {
        try {
            await api.delete('/notifications/delete-all');
            setNotifications([]);
        } catch (error) {
            console.error("Failed to clear notifications", error);
        }
    };

    const refreshNotifications = () => {
        fetchNotifications();
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                markAsRead,
                markAllAsRead,
                clearAllNotifications,
                refreshNotifications,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};
